import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addWeeks, subWeeks, startOfDay, startOfMonth, endOfMonth, addMonths, subMonths, addDays, subDays } from 'date-fns';
import MoverAvailabilityEditor from './MoverAvailabilityEditor.jsx';
import { apiFetch } from '../utils/apiFetch.js';

const EnhancedMoverCalendar = ({ token }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEventForm, setNewEventForm] = useState({ title: '', start: '', end: '', customer: '', location: '', notes: '' });
  const [savingEvent, setSavingEvent] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewMode, setViewMode] = useState(isMobile ? 'today' : 'week');

  // Fetch real calendar events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const headers = {};
        if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
        const res = await apiFetch('/api/calendar/me', { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // API returns { jobs: [...] }
        const raw = Array.isArray(data) ? data : (data.jobs || []);
        const normalized = raw.map(e => ({
          ...e,
          id: e._id || e.id,
          title: e.title || e.jobNumber || 'Booking',
          customer: e.customerName || e.customer || '',
          type: e.serviceType || e.type || 'moving',
          startTime: new Date(e.start || e.moveDate),
          endTime: new Date(e.end || e.start || e.moveDate),
          location: e.origin && e.destination ? `${e.origin} → ${e.destination}` : (e.location || ''),
          crew: e.crew || [],
          priority: e.priority || 'medium',
          notes: e.notes || '',
        }));
        setEvents(normalized);
      } catch (err) {
        console.error('Calendar fetch error:', err);
        setEvents([]);
      }
    };
    fetchEvents();
  }, [token]);

  useEffect(() => {
    if (isMobile && viewMode === 'month') {
      setViewMode('today');
    }
  }, [isMobile, viewMode]);

  const getWeekDays = () => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => 
      isSameDay(event.startTime, day)
    );
  };

  const getEventColor = (event) => {
    switch (event.status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'primary';
    }
  };

  const visibleDays = viewMode === 'today' ? [currentDate] : getWeekDays();
  const calendarTitle = viewMode === 'today'
    ? format(currentDate, 'EEEE, MMM d, yyyy')
    : viewMode === 'week'
      ? `${format(startOfWeek(currentDate), 'MMM d')} - ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`
      : format(currentDate, 'MMMM yyyy');

  const handlePrevPeriod = () => {
    setCurrentDate(viewMode === 'today' ? subDays(currentDate, 1) : viewMode === 'week' ? subWeeks(currentDate, 1) : subMonths(currentDate, 1));
  };

  const handleNextPeriod = () => {
    setCurrentDate(viewMode === 'today' ? addDays(currentDate, 1) : viewMode === 'week' ? addWeeks(currentDate, 1) : addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setViewMode('today');
  };

  const renderTimeSlots = () => {
    const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 8 PM
    const daysToRender = visibleDays;

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: `64px repeat(${daysToRender.length}, minmax(${viewMode === 'today' ? 260 : 92}px, 1fr))`, height: 610, overflow: 'auto', bgcolor: '#F8FAFC' }}>
        {/* Time column */}
        <Box sx={{ borderRight: '1px solid #D9E2EC', bgcolor: '#fff' }}>
            {hours.map(hour => (
              <Box
                key={hour}
                sx={{
                  height: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid #E6EDF3',
                  fontSize: '0.75rem',
                  color: '#6B7C93',
                  fontWeight: 700,
                }}
              >
                {format(new Date().setHours(hour, 0), 'h a')}
              </Box>
            ))}
        </Box>

        {/* Days columns */}
        {daysToRender.map((day, dayIndex) => (
          <Box key={dayIndex} sx={{ borderRight: dayIndex < daysToRender.length - 1 ? '1px solid #E6EDF3' : 0, bgcolor: isSameDay(day, new Date()) ? '#ECFEFF' : '#fff' }}>
              {hours.map(hour => {
                const slotStart = startOfDay(day);
                slotStart.setHours(hour);
                const dayEvents = getEventsForDay(day);
                const slotEvents = dayEvents.filter(event => 
                  event.startTime.getHours() === hour
                );

                return (
                  <Box
                    key={hour}
                    sx={{
                      height: 52,
                      borderBottom: '1px solid #E6EDF3',
                      position: 'relative',
                      cursor: 'pointer',
                      backgroundImage: 'linear-gradient(to right, rgba(217,226,236,0.35) 1px, transparent 1px)',
                      backgroundSize: '50% 100%',
                      '&:hover': {
                        backgroundColor: '#F0FDFA',
                      },
                    }}
                    onClick={() => setDialogOpen(true)}
                  >
                    {slotEvents.map(event => (
                      <Card
                        key={event.id}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          right: 2,
                          minHeight: 46,
                          zIndex: 1,
                          cursor: 'pointer',
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: `${getEventColor(event)}.main`,
                          borderLeft: 5,
                          boxShadow: '0 8px 18px rgba(16,42,67,0.12)',
                          borderRadius: 1.5,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                        }}
                      >
                        <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {format(event.startTime, 'h:mm a')}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                );
              })}
          </Box>
        ))}
      </Box>
    );
  };

  const renderDayHeaders = () => {
    const daysToRender = visibleDays;
    
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: `64px repeat(${daysToRender.length}, minmax(${viewMode === 'today' ? 260 : 92}px, 1fr))`, borderBottom: '1px solid #D9E2EC', bgcolor: '#fff' }}>
        <Box sx={{ height: 70, borderRight: '1px solid #D9E2EC' }} />
        {daysToRender.map((day, index) => (
          <Box key={index}>
            <Box
              sx={{
                textAlign: 'center',
                py: 1.25,
                px: 1,
                borderRight: index < daysToRender.length - 1 ? '1px solid #E6EDF3' : 0,
                bgcolor: isSameDay(day, new Date()) ? '#DDFCF7' : 'transparent',
              }}
            >
              <Typography variant="caption" sx={{ color: '#627D98', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.7 }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 850, color: isSameDay(day, new Date()) ? '#087F8C' : '#102A43' }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMonthGrid = () => {
    const monthDays = eachDayOfInterval({ start: startOfWeek(startOfMonth(currentDate)), end: endOfWeek(endOfMonth(currentDate)) });
    return (
      <Grid container columns={7} sx={{ bgcolor: '#fff' }}>
        {monthDays.map(day => {
          const dayEvents = getEventsForDay(day);
          const inMonth = day.getMonth() === currentDate.getMonth();
          return (
            <Grid item xs={1} key={day.toISOString()} sx={{ width: '14.285%', minHeight: 118, borderRight: '1px solid #E6EDF3', borderBottom: '1px solid #E6EDF3', p: 1, bgcolor: inMonth ? '#fff' : '#F8FAFC' }}>
              <Typography variant="caption" sx={{ color: isSameDay(day, new Date()) ? '#087F8C' : inMonth ? '#52606D' : '#9FB3C8', fontWeight: 800 }}>
                {format(day, 'd')}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {dayEvents.slice(0, 3).map(event => (
                  <Box key={event.id} onClick={() => setSelectedEvent(event)} sx={{ px: 0.75, py: 0.5, borderRadius: 1, bgcolor: `${getEventColor(event)}.50`, borderLeft: 3, borderColor: `${getEventColor(event)}.main`, cursor: 'pointer', overflow: 'hidden' }}>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 700 }}>{event.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{format(event.startTime, 'h:mm a')}</Typography>
                  </Box>
                ))}
                {dayEvents.length > 3 && <Typography variant="caption" color="text.secondary">+{dayEvents.length - 3} more</Typography>}
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderMobileAgenda = () => {
    const daysToRender = viewMode === 'week' ? visibleDays : [currentDate];

    return (
      <Stack spacing={1.5}>
        {daysToRender.map(day => {
          const dayEvents = getEventsForDay(day);
          return (
            <Paper key={day.toISOString()} elevation={0} sx={{ border: '1px solid #D9E2EC', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: isSameDay(day, new Date()) ? '#ECFEFF' : '#fff', borderBottom: '1px solid #E6EDF3' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 850, color: '#102A43' }}>{format(day, 'EEEE')}</Typography>
                <Typography variant="caption" sx={{ color: '#627D98', fontWeight: 700 }}>{format(day, 'MMM d, yyyy')}</Typography>
              </Box>
              <Stack spacing={1} sx={{ p: 1.5 }}>
                {dayEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No moves scheduled.</Typography>
                ) : dayEvents.map(event => (
                  <Card key={event.id} variant="outlined" onClick={() => setSelectedEvent(event)} sx={{ borderRadius: 2, borderLeft: 5, borderColor: `${getEventColor(event)}.main`, cursor: 'pointer' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#102A43' }}>{event.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{format(event.startTime, 'h:mm a')}</Typography>
                        </Box>
                        <Chip label={event.status || event.type} color={getEventColor(event)} size="small" sx={{ textTransform: 'capitalize' }} />
                      </Box>
                      {event.location && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{event.location}</Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 2.5, md: 4 }, minHeight: '100%', bgcolor: '#F4F7FB' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2, flexWrap: 'wrap', mb: 2.5, p: { xs: 2, md: 3.5 }, borderRadius: { xs: 2, md: 3 },
          color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)',
          boxShadow: '0 12px 28px rgba(16,42,67,0.18)',
        }}>
          <Box>
            <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>OPERATIONS</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>Schedule</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5, display: { xs: 'none', sm: 'block' } }}>Keep your team and customer commitments in sync.</Typography>
          </Box>
          
          {!isMobile && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.45)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              New Event
            </Button>
          )}
        </Box>

        {/* Calendar Controls */}
        <Paper elevation={0} sx={{ p: { xs: 1, md: 1.5 }, border: '1px solid #D9E2EC', borderRadius: 2.5, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePrevPeriod}>
                <PrevIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1, minWidth: { xs: 0, sm: 200 }, textAlign: 'center', fontWeight: 750, color: '#102A43', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {calendarTitle}
              </Typography>
              <IconButton onClick={handleNextPeriod}>
                <NextIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant={viewMode === 'today' ? 'contained' : 'outlined'} size="small" onClick={handleToday} sx={{ borderRadius: 1.5 }}>
                Today
              </Button>
              <Button
                variant={viewMode === 'week' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('week')}
                sx={{ borderRadius: 1.5 }}
              >
                Week
              </Button>
              <Button
                variant={viewMode === 'month' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('month')}
                sx={{ borderRadius: 1.5, display: { xs: 'none', md: 'inline-flex' } }}
              >
                Month
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '460px minmax(760px, 1fr)' },
        gap: 2.5,
        alignItems: 'start',
        justifyContent: 'start',
        maxWidth: '100%',
      }}>
        {isMobile ? (
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid #D9E2EC', borderRadius: 2.5, overflow: 'hidden', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fff', px: 2 }}>
              <Typography variant="subtitle1" fontWeight={800}>Availability</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1.5, bgcolor: '#F4F7FB' }}>
              <MoverAvailabilityEditor token={token} />
            </AccordionDetails>
          </Accordion>
        ) : (
          <MoverAvailabilityEditor token={token} />
        )}

        {/* Calendar Grid */}
        <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #D9E2EC', borderRadius: 2.5, bgcolor: '#fff', minWidth: 0 }}>
          {isMobile ? renderMobileAgenda() : viewMode === 'month' ? renderMonthGrid() : <>{renderDayHeaders()}{renderTimeSlots()}</>}
        </Paper>
      </Box>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
          onClick={() => setDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Event Details Dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedEvent.title}</Typography>
                <Chip
                  label={selectedEvent.status}
                  color={getEventColor(selectedEvent)}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                    <Typography>{selectedEvent.customer}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TimeIcon sx={{ color: 'text.secondary' }} />
                    <Typography>
                      {format(selectedEvent.startTime, 'MMM dd, h:mm a')} - {format(selectedEvent.endTime, 'h:mm a')}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationIcon sx={{ color: 'text.secondary' }} />
                    <Typography>{selectedEvent.location}</Typography>
                  </Box>
                </Grid>
                
                {selectedEvent.crew.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Assigned Crew:</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedEvent.crew.map((member, index) => (
                        <Chip
                          key={index}
                          label={member}
                          size="small"
                          avatar={<Avatar sx={{ width: 20, height: 20 }}>{member.charAt(0)}</Avatar>}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                
                {selectedEvent.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Notes:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEvent(null)}>Close</Button>
              <Button
                variant="contained"
                onClick={() => {
                  // pre-fill the create dialog with this event's data for editing
                  const toLocalDT = (d) => new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                  setNewEventForm({
                    title: selectedEvent.title,
                    start: toLocalDT(selectedEvent.startTime),
                    end: toLocalDT(selectedEvent.endTime),
                    customer: selectedEvent.customer || '',
                    location: selectedEvent.location || '',
                    notes: selectedEvent.notes || '',
                    _editId: selectedEvent.id,
                  });
                  setSelectedEvent(null);
                  setDialogOpen(true);
                }}
              >
                Edit Event
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* New Event Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Event Title" placeholder="Enter event title"
                value={newEventForm.title} onChange={e => setNewEventForm(f => ({ ...f, title: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Start Time" type="datetime-local" InputLabelProps={{ shrink: true }}
                value={newEventForm.start} onChange={e => setNewEventForm(f => ({ ...f, start: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="End Time" type="datetime-local" InputLabelProps={{ shrink: true }}
                value={newEventForm.end} onChange={e => setNewEventForm(f => ({ ...f, end: e.target.value }))} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Customer" placeholder="Customer name"
                value={newEventForm.customer} onChange={e => setNewEventForm(f => ({ ...f, customer: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location" placeholder="Event location" multiline rows={2}
                value={newEventForm.location} onChange={e => setNewEventForm(f => ({ ...f, location: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" placeholder="Additional notes" multiline rows={3}
                value={newEventForm.notes} onChange={e => setNewEventForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingEvent}
            onClick={async () => {
              if (!newEventForm.title || !newEventForm.start || !newEventForm.end) return;
              setSavingEvent(true);
              try {
                const headers = {};
                if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
                const isEdit = !!newEventForm._editId;
                const url = isEdit ? `/api/calendar/events/${newEventForm._editId}` : '/api/calendar/events';
                const res = await apiFetch(url, {
                  method: isEdit ? 'PUT' : 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json', ...headers },
                  body: JSON.stringify(newEventForm),
                });
                const data = await res.json();
                if (res.ok) {
                  const normalized = { ...data, id: data._id, startTime: new Date(data.start), endTime: new Date(data.end) };
                  setEvents(prev => isEdit
                    ? prev.map(e => e.id === newEventForm._editId ? normalized : e)
                    : [...prev, normalized]);
                  setDialogOpen(false);
                  setNewEventForm({ title: '', start: '', end: '', customer: '', location: '', notes: '' });
                }
              } finally {
                setSavingEvent(false);
              }
            }}
          >
            {savingEvent ? 'Saving…' : newEventForm._editId ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedMoverCalendar;